import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bug, Server } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { getPluginLog, type PluginErrorLogDetail } from "@/services/pluginLogsApi";
import { SeverityBadge } from "./severity";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="break-all text-right text-sm">{children}</span>
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-xs">
      {children}
    </pre>
  );
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface Props {
  /** Log id to display; the sheet is open iff this is non-null. */
  logId: number | null;
  onClose: () => void;
}

export function PluginLogDetailSheet({ logId, onClose }: Props) {
  const navigate = useNavigate();
  const [log, setLog] = useState<PluginErrorLogDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (logId == null) {
      setLog(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPluginLog(navigate, logId);
        if (!cancelled) setLog(data);
      } catch (e: unknown) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logId, navigate]);

  return (
    <Sheet open={logId != null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            <Bug size={16} />
            {log ? (
              <code className="text-sm">
                {log.source}/{log.reason}
              </code>
            ) : (
              <span className="text-sm">Log plugin</span>
            )}
            {log && <SeverityBadge severity={log.severity} />}
          </SheetTitle>
          <SheetDescription>
            Erreur worker/plugin — message complet, stack et contexte.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <p className="mt-4 text-sm text-muted-foreground">Chargement…</p>
        )}
        {error && (
          <p className="mt-4 flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle size={14} /> {error}
          </p>
        )}

        {log && !loading && (
          <div className="mt-4 space-y-5">
            <section>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <Server size={15} /> Métadonnées
              </h3>
              <div className="space-y-1.5">
                <Row label="Date">{fmtDate(log.created_at)}</Row>
                <Row label="Worker">
                  <code className="text-xs">{log.worker_id || "—"}</code>
                </Row>
                <Row label="Source / motif">
                  <code className="text-xs">
                    {log.source}/{log.reason}
                  </code>
                </Row>
                <Row label="Sévérité">
                  <SeverityBadge severity={log.severity} />
                </Row>
                <Row label="Abonnement">{log.subscription_id ?? "—"}</Row>
                <Row label="User ID">
                  <code className="text-xs">{log.user_id || "—"}</code>
                </Row>
                <Row label="Version plugin">{log.plugin_version || "—"}</Row>
                <Row label="Échecs consécutifs">
                  {log.consecutive_failures ?? "—"}
                </Row>
              </div>
            </section>

            <Separator />

            <section>
              <h3 className="mb-2 text-sm font-semibold">Message</h3>
              {log.message ? (
                <CodeBlock>{log.message}</CodeBlock>
              ) : (
                <p className="text-sm text-muted-foreground">—</p>
              )}
            </section>

            {log.stack && (
              <section>
                <h3 className="mb-2 text-sm font-semibold">Stack</h3>
                <CodeBlock>{log.stack}</CodeBlock>
              </section>
            )}

            {log.context && Object.keys(log.context).length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold">Contexte</h3>
                <CodeBlock>{JSON.stringify(log.context, null, 2)}</CodeBlock>
              </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
